import { friendlyApiErrorMessage } from 'src/app/services/api-error-notifier.service';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Optional,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { MatDialogModule, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import * as d3 from 'd3';
import { catchError, of, Subscription } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { FilterService } from 'src/app/services/filter.service';
import { ProgressIndefinite } from '../../progress-indicator/indefinite';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

/** Report-type buckets that drive both the X tick format and the time
 *  converter used for the 1-unit domain padding on the brush. Mirrors the
 *  values produced by `FilterService.report_type`. */
export type D3BasicLinePeriod = 'D' | 'W' | 'M' | 'Q' | 'Y' | 'PCQ';

/** Chart props. All optional — defaults are owned by `loadApiData()` below. */
export interface D3BasicLineProps {
  xAxisVar?: string;
  yAxisVar?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  valueFormat?: 'value' | 'percentage';
  period?: D3BasicLinePeriod;
  color?: string;
  yAxisFormat?: string;
  showDots?: boolean;
  curve?: 'linear' | 'monotone';
  lineWidth?: number;
  dotRadius?: number;
  showBrush?: boolean;
  /** Switches the main-chart renderer. `'line'` (default) draws the
   *  stroked path + dots; `'bar'` draws vertical bars centered on each
   *  data point. Axes, brush overview, and tooltip stay shared. */
  chartType?: 'line' | 'bar';
  /** Same flag as `line-chart.component.ts` — when `false` (default), the
   *  X axis shows a tick at every actual data-point date (driven by
   *  `report_type`); when `true`, d3 picks ~6 evenly-spaced ticks. */
  d3AxisFormatting?: boolean;
}

/** Optional overrides from the caller (`MAT_DIALOG_DATA`). */
export interface D3BasicLinePopupData {
  heading?: string;
  /** Pre-loaded chart data. Wins over `apiKey` when both are provided.
   *  Pass `[]` to explicitly show the no-data state. */
  data?: any[];
  props?: D3BasicLineProps;
  /** Backend `api_key` posted with the data fetch. When provided (and `data`
   *  is not), the popup fetches the series via POST instead of using the
   *  local test set. Mirrors `BasicPopupGridDialogData.apiKey`. */
  apiKey?: string | number;
  /** Optional endpoint override; defaults to `apiService.executionUrl`. */
  endpoint?: string;
  /** Extra fields merged into the POST body alongside `filterService.baseQuery`. */
  payload?: Record<string, any>;
  /** `report_typ` override; defaults to `filterService.report_type` (or 'D'). */
  reportType?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

@Component({
    imports: [CommonModule, MatDialogModule, MatIconModule, ProgressIndefinite],
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-d3-basic-line-popup',
    templateUrl: './d3-basic-line-popup.component.html',
    styleUrls: ['./d3-basic-line-popup.component.scss']
})
export class D3BasicLinePopupComponent implements AfterViewInit, OnDestroy {
  // ─── view refs ────────────────────────────────────────────────────────────
  @ViewChild('gridLineChartContainer', { static: true }) gridLineChartContainer!: ElementRef;
  @ViewChild('linesChartTooltip') linesChartTooltip!: ElementRef | any;

  // ─── public state (template-bound) ────────────────────────────────────────
  divId = 'd3BasicLinePopupChart';
  heading = 'Trend';
  chartData: any[] = [];
  props!: Required<D3BasicLineProps>;
  loader = false;
  error_name = 'No data available';
  mytooltipData: any = null;
  tooltipVisible = false;
  /** Hides the chart host until the first plot has run at the dialog's final
   *  size. Avoids the visible "small → snap to big" glitch caused by the SVG
   *  rendering mid-open-animation. */
  chartReady = false;

  // ─── private state ────────────────────────────────────────────────────────
  private resizeTimer: any = null;
  private resizeObserver: ResizeObserver | null = null;
  private chartInitialized = false;
  private dataSub: Subscription | null = null;

  // ─── lookup tables (constant) ─────────────────────────────────────────────
  /** Tick format per period, matching basic-line-chart-grid / line-chart.
   *  Q uses the same `Q%q %Y` mask as PCQ so quarter buckets render as
   *  "Q1 2025", "Q2 2025", … on both the x-axis and the tooltip. */
  private readonly tickFormatMap: Record<D3BasicLinePeriod, string> = {
    D: '%d %b %y',
    W: '%d %b %y',
    M: '%b %Y',
    Q: 'Q%q %Y',
    Y: '%Y',
    PCQ: 'Q%q %Y',
  };

  /** d3 time interval per period — used to pad the brush domain by 1 unit
   *  on each end, the same "1-scale space last and first" that the source
   *  basic-line-chart-grid chart applies. */
  private readonly timeConverterMap: Record<D3BasicLinePeriod, d3.TimeInterval> = {
    D: d3.timeDay,
    W: d3.timeWeek,
    M: d3.timeMonth,
    Q: d3.timeMonth,
    Y: d3.timeYear,
    PCQ: d3.timeMonth,
  };

  // ─── lifecycle ────────────────────────────────────────────────────────────

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: D3BasicLinePopupData | null,
    private dialogRef: MatDialogRef<D3BasicLinePopupComponent>,
    private filterService: FilterService,
    private cdr: ChangeDetectorRef,
    private httpClient: HttpClient,
    private apiService: ApiService,
  ) {
    // Load data + props before the first change-detection pass so the
    // template sees the real `chartData` from the start. If we waited until
    // ngAfterViewInit, OnPush would already have rendered the empty-state
    // and wouldn't re-render after the mutation.
    this.loadApiData();
  }

  ngAfterViewInit(): void {
    this.initiateChart();
    this.chartInitialized = true;

    // Force-strip padding/max-height on every Material wrapper between the
    // chart host and the .cdk-overlay-pane. CSS overrides are fragile
    // across Material versions (mat-dialog-container vs mat-mdc-dialog-container
    // vs mdc-dialog__surface), so we walk up the DOM and zero them out via
    // inline styles — highest specificity, can't be overridden.
    // this.forceFullBleedPopup();

    // Defer the first plot AND the ResizeObserver attachment until the
    // dialog's open animation completes. Two reasons:
    //   1. Mid-animation, Material applies `transform: scale(...)`, so
    //      `getBoundingClientRect()` returns the scaled-down size — plotting
    //      then renders the chart too small.
    //   2. `ResizeObserver.observe()` fires synchronously with the current
    //      size on attachment. If attached in ngAfterViewInit, that initial
    //      callback paints the chart small, and the user sees it "snap" to
    //      full size when afterOpened later fires — the visible glitch.
    // Attaching the observer only after afterOpened means the chart is drawn
    // exactly once, at its final size.
    this.dialogRef.afterOpened().subscribe(() => {
      if (this.chartData.length) {
        this.plotChart();
      }
      this.chartReady = true;
      this.cdr.markForCheck();

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          clearTimeout(this.resizeTimer);
          this.resizeTimer = setTimeout(() => this.plotChart(), 50);
        });
        this.resizeObserver.observe(this.gridLineChartContainer.nativeElement);
      }
    });
  }

  /** Walk up from the chart host to the `.cdk-overlay-pane` and zero out
   *  every Material wrapper's padding / max-height in between. Inline
   *  styles win over any default Material stylesheet, so this gives us a
   *  reliable full-bleed popup regardless of which Material version is
   *  in play (legacy `mat-dialog-container` or the newer MDC versions). */
  // private forceFullBleedPopup(): void {
  //   let el: HTMLElement | null = this.gridLineChartContainer.nativeElement;
  //   while (el && !el.classList?.contains('cdk-overlay-pane')) {
  //     const tag = (el.tagName || '').toLowerCase();
  //     const isDialogWrapper =
  //       tag === 'mat-dialog-container' ||
  //       tag === 'mat-mdc-dialog-container' ||
  //       el.classList?.contains('mat-dialog-container') ||
  //       el.classList?.contains('mat-mdc-dialog-container') ||
  //       el.classList?.contains('mdc-dialog__container') ||
  //       el.classList?.contains('mdc-dialog__surface') ||
  //       el.classList?.contains('mat-mdc-dialog-surface') ||
  //       el.classList?.contains('mat-mdc-dialog-content') ||
  //       el.classList?.contains('mat-dialog-content');
  //     if (isDialogWrapper) {
  //       el.style.setProperty('padding', '0', 'important');
  //       el.style.setProperty('margin', '0', 'important');
  //       el.style.setProperty('width', '100%', 'important');
  //       el.style.setProperty('height', '100%', 'important');
  //       el.style.setProperty('max-width', '100%', 'important');
  //       el.style.setProperty('max-height', '100%', 'important');
  //       el.style.setProperty('overflow', 'hidden', 'important');
  //       el.style.setProperty('display', 'flex', 'important');
  //       el.style.setProperty('flex-direction', 'column', 'important');
  //       el.style.setProperty('box-sizing', 'border-box', 'important');
  //     }
  //     el = el.parentElement;
  //   }
  // }

  ngOnDestroy(): void {
    clearTimeout(this.resizeTimer);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.dataSub) {
      this.dataSub.unsubscribe();
      this.dataSub = null;
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  // ─── data + props ─────────────────────────────────────────────────────────

  /** Owns the chart data and chart props. Mirrors the loadApiData pattern
   *  used by `basic-line-chart-grid` — sets `this.chartData`, builds
   *  `this.props`, then triggers `plotChart()`. Caller overrides supplied
   *  via `MAT_DIALOG_DATA` are merged on top.
   *
   *  When `dialogData.apiKey` is provided (and `dialogData.data` is not),
   *  the chart series is fetched from the backend instead of using the
   *  bundled test set — mirrors `basic-popup-grid.component.ts`. The POST
   *  body merges `filterService.baseQuery` + caller's `payload` + the
   *  standard `api_key` / `report_typ` / `module_key` keys. */
  loadApiData(): void {
    this.loader = true;

    // 1) Test data per report_type. Each set picks dates that align with
    //    the bucket (weekly Mondays, month starts, quarter starts, year
    //    starts) so the x-axis tick format renders sensibly.
    let defaultData: any[] = [];
    // if (this.filterService?.report_type == 'W') {
    //   defaultData = [
    //     // Original 10 (Jan – early Mar 2025)
    //     { period: '2025-01-06', value: 11.2 },
    //     { period: '2025-01-13', value: 14.8 },
    //     { period: '2025-01-20', value: 9.5 },
    //     { period: '2025-01-27', value: 17.1 },
    //     { period: '2025-02-03', value: 21.4 },
    //     { period: '2025-02-10', value: 19.7 },
    //     { period: '2025-02-17', value: 24.3 },
    //     { period: '2025-02-24', value: 22.9 },
    //     { period: '2025-03-03', value: 27.6 },
    //     { period: '2025-03-10', value: 25.1 },
    //     // +50 more weekly points (Mar 2025 – Feb 2026) for brushing tests
    //     { period: '2025-03-17', value: 27.8 },
    //     { period: '2025-03-24', value: 30.4 },
    //     { period: '2025-03-31', value: 29.1 },
    //     { period: '2025-04-07', value: 32.5 },
    //     { period: '2025-04-14', value: 30.9 },
    //     { period: '2025-04-21', value: 33.7 },
    //     { period: '2025-04-28', value: 31.2 },
    //     { period: '2025-05-05', value: 35.4 },
    //     { period: '2025-05-12', value: 33.8 },
    //     { period: '2025-05-19', value: 36.7 },
    //     { period: '2025-05-26', value: 34.5 },
    //     { period: '2025-06-02', value: 37.9 },
    //     { period: '2025-06-09', value: 36.2 },
    //     { period: '2025-06-16', value: 39.8 },
    //     { period: '2025-06-23', value: 38.5 },
    //     { period: '2025-06-30', value: 41.3 },
    //     { period: '2025-07-07', value: 39.7 },
    //     { period: '2025-07-14', value: 43.2 },
    //     { period: '2025-07-21', value: 41.9 },
    //     { period: '2025-07-28', value: 44.5 },
    //     { period: '2025-08-04', value: 42.8 },
    //     { period: '2025-08-11', value: 40.6 },
    //     { period: '2025-08-18', value: 38.1 },
    //     { period: '2025-08-25', value: 35.7 },
    //     { period: '2025-09-01', value: 33.4 },
    //     { period: '2025-09-08', value: 31.2 },
    //     { period: '2025-09-15', value: 28.9 },
    //     { period: '2025-09-22', value: 26.5 },
    //     { period: '2025-09-29', value: 24.1 },
    //     { period: '2025-10-06', value: 22.4 },
    //     { period: '2025-10-13', value: 20.8 },
    //     { period: '2025-10-20', value: 19.5 },
    //     { period: '2025-10-27', value: 17.9 },
    //     { period: '2025-11-03', value: 16.4 },
    //     { period: '2025-11-10', value: 18.2 },
    //     { period: '2025-11-17', value: 21.6 },
    //     { period: '2025-11-24', value: 24.9 },
    //     { period: '2025-12-01', value: 27.3 },
    //     { period: '2025-12-08', value: 29.8 },
    //     { period: '2025-12-15', value: 32.4 },
    //     { period: '2025-12-22', value: 35.1 },
    //     { period: '2025-12-29', value: 37.6 },
    //     { period: '2026-01-05', value: 34.2 },
    //     { period: '2026-01-12', value: 31.8 },
    //     { period: '2026-01-19', value: 29.4 },
    //     { period: '2026-01-26', value: 27.1 },
    //     { period: '2026-02-02', value: 25.8 },
    //     { period: '2026-02-09', value: 23.5 },
    //     { period: '2026-02-16', value: 21.9 },
    //     { period: '2026-02-23', value: 20.4 },
    //   ];
    // } else if (this.filterService?.report_type == 'M') {
    //   defaultData = [
    //     { period: '2025-01-01', value: 12.4 },
    //     { period: '2025-02-01', value: 18.1 },
    //     { period: '2025-03-01', value: 9.7 },
    //     { period: '2025-04-01', value: 22.5 },
    //     { period: '2025-05-01', value: 28.3 },
    //     { period: '2025-06-01', value: 17.2 },
    //     { period: '2025-07-01', value: 31.0 },
    //   ];
    // } else if (this.filterService?.report_type == 'Q') {
    //   defaultData = [
    //     { period: '2024-01-01', value: 14.5 },
    //     { period: '2024-04-01', value: 21.8 },
    //     { period: '2024-07-01', value: 18.3 },
    //     { period: '2024-10-01', value: 26.7 },
    //     { period: '2025-01-01', value: 29.4 },
    //     { period: '2025-04-01', value: 33.1 },
    //   ];
    // } else if (this.filterService?.report_type == 'Y') {
    //   defaultData = [
    //     { period: '2020-01-01', value: 8.6 },
    //     { period: '2021-01-01', value: 13.2 },
    //     { period: '2022-01-01', value: 19.5 },
    //     { period: '2023-01-01', value: 24.1 },
    //     { period: '2024-01-01', value: 29.8 },
    //     { period: '2025-01-01', value: 34.7 },
    //   ];
    // } else {
    //   // Fallback: daily-ish series (also covers D / unknown).
    //   defaultData = [
    //     { period: '2025-07-01', value: 12.4 },
    //     { period: '2025-07-02', value: 14.6 },
    //     { period: '2025-07-03', value: 11.9 },
    //     { period: '2025-07-04', value: 17.2 },
    //     { period: '2025-07-05', value: 20.1 },
    //     { period: '2025-07-06', value: 18.4 },
    //     { period: '2025-07-07', value: 22.7 },
    //   ];
    // }
    // 2) Props — period defaults to whatever the dashboard is showing
    const reportType = String(this.filterService?.report_type || 'M').toUpperCase();
    const allowed: D3BasicLinePeriod[] = ['D', 'W', 'M', 'Q', 'Y', 'PCQ'];
    const periodFromService: D3BasicLinePeriod = (allowed.includes(
      reportType as D3BasicLinePeriod,
    )
      ? reportType
      : 'M') as D3BasicLinePeriod;

    const defaultProps: Required<D3BasicLineProps> = {
      xAxisVar: 'period',
      yAxisVar: 'value',
      xAxisLabel: 'Period',
      yAxisLabel: 'Completeness',
      valueFormat: 'percentage',
      period: periodFromService,
      color: '#BB7EDA',
      yAxisFormat: '.2~s',
      showDots: true,
      curve: 'linear',
      lineWidth: 3,
      dotRadius: 4,
      showBrush: true,
      chartType: 'bar',
      d3AxisFormatting: false, // tick per data point — matches line-chart
    };

    // 3) Merge optional caller overrides
    const override = this.dialogData || {};
    this.heading = override.heading || 'Trend';
    this.props = { ...defaultProps, ...(override.props || {}) };

    // 4) Data source selection. Priority:
    //    a) explicit `data` from caller (including `[]` for no-data state)
    //    b) `apiKey` → fetch from backend
    //    c) bundled test data fallback
    if (Array.isArray(override.data)) {
      this.chartData = override.data.slice();
      this.error_name = this.chartData.length ? 'No data available' : 'There is no data for this period';
      this.loader = false;
      this.cdr.markForCheck();
      if (this.chartInitialized && this.chartData.length) {
        this.plotChart();
      }
      return;
    }

    if (override.apiKey !== undefined && override.apiKey !== null && override.apiKey !== '') {
      this.chartData = [];
      this.fetchApiData(override);
      return;
    }

    this.chartData = [...defaultData];
    this.error_name = this.chartData.length ? 'No data available' : 'There is no data for this period';
    this.loader = false;
    this.cdr.markForCheck();
    if (this.chartInitialized && this.chartData.length) {
      this.plotChart();
    }
  }

  /** Fire the POST request and re-render once the data lands. Mirrors
   *  `basic-popup-grid.loadData()` — same body shape (baseQuery + caller
   *  payload + api_key / report_typ / module_key) and same response
   *  normalisation (array | { data: [] }). */
  private fetchApiData(override: D3BasicLinePopupData): void {
    if (this.dataSub) {
      this.dataSub.unsubscribe();
      this.dataSub = null;
    }

    const body: any = {
      ...(this.filterService.baseQuery || {}),
      ...(override.payload || {}),
      api_key: override.apiKey,
      report_typ: override.reportType || this.filterService.report_type || 'D',
      module_key: this.apiService.getModuleKey(),
    };

    const endpoint = override.endpoint || this.apiService.executionUrl;
    const url = `${this.apiService.apiBaseUrl}/${endpoint}`;

    this.dataSub = this.httpClient
      .post<any>(url, body)
      .pipe(
        catchError((err: any) => {
          this.error_name =
            friendlyApiErrorMessage(err);
          return of([]);
        }),
      )
      .subscribe((res: any) => {
        const rows: any[] = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        this.chartData = rows;
        if (!rows.length && this.error_name === 'No data available') {
          this.error_name = 'There is no data for this period';
        }
        this.loader = false;
        this.cdr.markForCheck();
        if (this.chartInitialized && this.chartData.length) {
          this.plotChart();
        }
      });
  }

  // ─── chart ────────────────────────────────────────────────────────────────

  /** One-time scaffold: create the SVG and all named groups/paths the
   *  data-driven `plotChart()` later updates. */
  private initiateChart(): void {
    const myClass = this.divId;
    const mySvg = d3
      .select(this.gridLineChartContainer.nativeElement)
      .append('svg')
      .attr('id', 'svg_' + myClass)
      .attr('width', '100%')
      .attr('height', '100%')
      .style('display', 'block') // kill the inline-element baseline gap so the SVG sits flush at the bottom
      .style('background-color', 'white');

    const defs = mySvg.append('defs');
    defs
      .append('clipPath')
      .attr('id', 'bmlBrushClip' + myClass)
      .append('rect')
      .attr('id', 'bmlBrushClipRect' + myClass);

    // drop-shadow filter for brush handles
    const filter: any = defs
      .append('filter')
      .attr('id', 'd3blp-drop-shadow')
      .attr('width', 10)
      .attr('height', 24);
    filter.append('feGaussianBlur').attr('in', 'SourceAlpha').attr('stdDeviation', 1).attr('result', 'blur');
    filter.append('feOffset').attr('in', 'blur').attr('dx', 1).attr('dy', 1).attr('result', 'offsetBlur');
    filter
      .append('feFlood')
      .attr('in', 'offsetBlur')
      .attr('flood-color', '#000000')
      .attr('flood-opacity', 0.4)
      .attr('result', 'offsetColor');
    filter
      .append('feComposite')
      .attr('in', 'offsetColor')
      .attr('in2', 'offsetBlur')
      .attr('operator', 'in')
      .attr('result', 'offsetBlur');
    const feMerge = filter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'offsetBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');

    // main chart layers
    mySvg.append('text').attr('id', 'xAxisLabel' + myClass);
    mySvg.append('text').attr('id', 'yAxisLabel' + myClass);
    mySvg.append('g').attr('id', 'xAxis' + myClass).attr('class', 'axis' + myClass);
    mySvg.append('g').attr('id', 'yAxis' + myClass).attr('class', 'axis' + myClass);
    mySvg.append('path').attr('id', 'linePath' + myClass);
    mySvg.append('g').attr('id', 'dotsGroup' + myClass);
    mySvg.append('g').attr('id', 'barsGroup' + myClass);
    // vertical hover guide — toggled visible from the hover-capture rect
    // below. Same dashed style as the source `basic-line-chart-grid`.
    mySvg
      .append('line')
      .attr('id', 'hoverLine' + myClass)
      .attr('pointer-events', 'none')
      .attr('visibility', 'hidden')
      .attr('stroke', '#D6DCE3') // light grey — soft cross-hair, not a focus line
      .attr('stroke-dasharray', '4,4')
      .attr('stroke-width', 1);
    // Transparent rect over the chart area that catches mousemove anywhere
    // (not just on dots) and snaps the guide + tooltip to the nearest data
    // point. Mirrors the `#mouseoverRect` pattern in basic-line-chart-grid.
    mySvg.append('rect').attr('id', 'hoverCapture' + myClass).attr('fill', 'transparent');

    // brush layers
    mySvg.append('g').attr('id', 'brushLinesGroup' + myClass);
    mySvg.append('g').attr('id', 'brushDotsGroup' + myClass);
    mySvg.append('g').attr('id', 'brushBarsGroup' + myClass);
    mySvg.append('g').attr('id', 'brushGroup' + myClass);
    mySvg.append('line').attr('class', 'handleLines' + myClass + ' handleLeftLine1' + myClass);
    mySvg.append('line').attr('class', 'handleLines' + myClass + ' handleLeftLine2' + myClass);
    mySvg.append('line').attr('class', 'handleLines' + myClass + ' handleRightLine1' + myClass);
    mySvg.append('line').attr('class', 'handleLines' + myClass + ' handleRightLine2' + myClass);
  }

  /** Data-driven render. Safe to call repeatedly — scales, axes, line, dots
   *  and brush are all re-bound to current `this.chartData` / `this.props`. */
  private plotChart(): void {
    if (!this.chartInitialized) return;
    const myChart = this;
    const myClass = this.divId;
    const containerEl = this.gridLineChartContainer.nativeElement;
    const mySvg: any = d3.select('#svg_' + myClass);
    if (!containerEl || mySvg.empty()) return;

    const rect = containerEl.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    if (!width || !height) return;

    const props = this.props;
    const allData = (this.chartData || [])
      .map((row) => ({
        x: this.toDate(row?.[props.xAxisVar]),
        y: Number(row?.[props.yAxisVar]) || 0,
      }))
      .filter((d) => d.x !== '' && d.x !== null && d.x !== undefined);

    if (!allData.length) {
      this.error_name = 'No data available';
      return;
    }

    const isDateAxis = allData.every((d) => d.x instanceof Date && !isNaN((d.x as Date).getTime()));
    const showBrush = !!(props.showBrush && isDateAxis);

    // Predict whether the x-axis labels will need to be slanted, so we can
    // grow the bottom layout band in advance and avoid the "Period" caption
    // crashing into rotated tick text. Heuristic: estimate the formatted
    // label width per period and check if all dates would fit upright.
    // The actual rotate-or-not decision later in `drawChart()` uses
    // measured DOM widths, so this is just sizing.
    const approxLabelWidthMap: Record<D3BasicLinePeriod, number> = {
      D: 54, W: 54, M: 48, Q: 48, Y: 24, PCQ: 48,
    };
    const approxLabelWidth =
      approxLabelWidthMap[(props.period || 'M') as D3BasicLinePeriod] || 50;
    // Provisional chart-width estimate (real one is computed below). Good
    // enough for the upfront slant prediction.
    const provisionalLeft = props.yAxisLabel ? 60 : 44;
    const provisionalChartWidth = Math.max(0, width - provisionalLeft - 12);
    const expectsSlant =
      !props.d3AxisFormatting &&
      isDateAxis &&
      allData.length > 0 &&
      allData.length * approxLabelWidth > provisionalChartWidth;

    // ─── layout (stacked top → bottom inside the SVG) ───────────────────────
    // Each value below describes one vertical band of the SVG. Computing
    // them explicitly avoids the overlap that came from sharing offsets
    // between the X label and the brush.
    const layout = {
      left: props.yAxisLabel ? 60 : 44,
      // Tight right margin — just enough to keep the last X tick label
      // from being clipped. The chart + brush now extend to the popup
      // edge so there's no perceived "right side space".
      right: 12,
      top: 16,
      // Slanted labels extend `width × sin(45°) ≈ width × 0.7` pixels
      // downward from the axis baseline. Reserve enough room so the
      // bottom of the rotated label doesn't collide with the "Period"
      // caption below it.
      xAxisTickH: expectsSlant ? 48 : 20,
      // 24px (was 18) gives the caption a comfortable band so the text
      // doesn't visually touch the brush even if the slant prediction
      // turns out wrong (predicted slant + upright render leaves the
      // caption hanging in space — extra room here keeps it readable).
      xAxisLabelH: props.xAxisLabel ? 24 : 0,
      // 16px (was 8) — buffer between the caption baseline and the top
      // of the brush row so the two never visually merge.
      gapAboveBrush: showBrush ? 16 : 0,
      brushRectH: showBrush ? 28 : 0,
      bottomPad: 8,
    };

    const bottomTotal =
      layout.xAxisTickH +
      layout.xAxisLabelH +
      layout.gapAboveBrush +
      layout.brushRectH +
      layout.bottomPad;

    mySvg.attr('width', width).attr('height', height);

    const chartWidth = width - layout.left - layout.right;
    const chartTopY = layout.top;
    const chartBottomY = height - bottomTotal;
    const chartHeight = chartBottomY - chartTopY;
    if (chartWidth <= 0 || chartHeight <= 0) return;

    // band y-positions
    const xAxisLabelY = chartBottomY + layout.xAxisTickH + 12;
    const brushY = chartBottomY + layout.xAxisTickH + layout.xAxisLabelH + layout.gapAboveBrush;

    // ─── scales (with 2-unit padding on first/last for breathing room) ─────
    const timePeriod = (props.period || 'M') as D3BasicLinePeriod;
    const timeConverter = this.timeConverterMap[timePeriod] || d3.timeMonth;
    const xTickFormat = isDateAxis
      ? d3.timeFormat(this.tickFormatMap[timePeriod] || '%b %Y')
      : null;

    // ── tick values / count — same pattern as line-chart.component.ts.
    // When `d3AxisFormatting` is false (default) we pin a tick to every
    // actual data-point date; when true, d3 picks ~6 evenly-spaced ticks.
    const dateGroupDates: Date[] = isDateAxis
      ? (allData.map((d) => d.x as Date).sort((a, b) => d3.ascending(a, b)))
      : [];
    const tickValues: Date[] | null = props.d3AxisFormatting ? null : dateGroupDates;
    const tickCount: number | null = props.d3AxisFormatting ? 6 : null;

    let xScale: any;
    let xScaleAll: any;
    let xScaleBrush: any;
    if (isDateAxis) {
      const xExtent = d3.extent(allData, (d) => d.x as Date) as [Date, Date];
      // 2 units of padding on each end — one extra "period" of room
      // before the first data point and after the last, so the dots
      // don't sit on the chart edge.
      const paddedDomain: [Date, Date] = [
        timeConverter.offset(xExtent[0], -2),
        timeConverter.offset(xExtent[1], +2),
      ];
      xScaleAll = d3.scaleTime().range([0, chartWidth]).domain(paddedDomain);
      xScale = d3.scaleTime().range([0, chartWidth]).domain(paddedDomain);
      xScaleBrush = d3.scaleTime().range([0, chartWidth]).domain(paddedDomain);
    } else {
      const domain = allData.map((d) => String(d.x));
      xScale = d3.scalePoint().range([0, chartWidth]).domain(domain).padding(0.5);
      xScaleAll = xScale;
      xScaleBrush = xScale;
    }

    const yMaxRaw = (d3.max(allData, (d) => d.y) as number) || 1;
    const yMax = yMaxRaw * 1.1;
    const yScale = d3.scaleLinear().domain([0, yMax]).range([chartHeight, 0]);
    const yScaleBrush = d3.scaleLinear().domain([0, yMax]).range([layout.brushRectH, 0]);

    const xAccessor = (d: any) =>
      isDateAxis ? xScale(d.x) : xScale(String(d.x)) || 0;
    const xBrushAccessor = (d: any) =>
      isDateAxis ? xScaleBrush(d.x) : xScaleBrush(String(d.x)) || 0;

    const curve = props.curve === 'monotone' ? d3.curveMonotoneX : d3.curveLinear;
    const line: any = d3.line<any>().curve(curve).x(xAccessor).y((d: any) => yScale(d.y));
    const lineBrush: any = d3
      .line<any>()
      .curve(curve)
      .x(xBrushAccessor)
      .y((d: any) => yScaleBrush(d.y));

    // clip rect (covers the chart area so the line/dots don't bleed outside)
    mySvg
      .select('#bmlBrushClipRect' + myClass)
      .style('width', chartWidth)
      .style('height', height)
      .attr('transform', 'translate(0,-20)');

    // ─── axis labels ────────────────────────────────────────────────────────
    d3.select('#xAxisLabel' + myClass)
      .attr('x', layout.left + chartWidth / 2)
      .attr('y', xAxisLabelY)
      .attr('text-anchor', 'middle')
      .attr('font-family', 'Poppins')
      .attr('fill', '#101D42')
      .attr('font-size', '12px')
      .text(props.xAxisLabel || '');

    d3.select('#yAxisLabel' + myClass)
      .attr('transform', 'translate(14,' + (chartTopY + chartHeight / 2) + ') rotate(-90)')
      .attr('text-anchor', 'middle')
      .attr('font-family', 'Poppins')
      .attr('fill', '#101D42')
      .attr('font-size', '12px')
      .text(props.yAxisLabel || '');

    // ─── Y axis ─────────────────────────────────────────────────────────────
    const yTickFormat = this.makeYTickFormatter();
    mySvg
      .select('#yAxis' + myClass)
      .call(d3.axisLeft(yScale).tickSizeOuter(0).ticks(6).tickFormat(yTickFormat as any))
      .attr('transform', 'translate(' + layout.left + ',' + chartTopY + ')');

    mySvg
      .selectAll('#yAxis' + myClass + ' line')
      .attr('x1', '0')
      .attr('x2', chartWidth)
      .style('stroke', '#F0F3F6')
      .style('stroke-width', 1);

    mySvg.selectAll('#yAxis' + myClass + ' text').style('fill', '#101D42').style('font-size', '12px');

    // ─── brush (matches basic-line-chart-grid) ──────────────────────────────
    let brush: any = null;
    if (showBrush) {
      const brushTransform = `translate(${layout.left},${brushY})`;

      brush = d3
        .brushX()
        .handleSize(10)
        .extent([
          [0, 0],
          [chartWidth, layout.brushRectH],
        ])
        .on('start brush end', brushed);

      if (props.chartType === 'bar') {
        // ── Brush overview as bars (matches main chart). Strip the
        //    line + dots variant so they don't paint underneath. ───
        mySvg.select('#brushLinesGroup' + myClass).selectAll('*').remove();
        mySvg.select('#brushDotsGroup' + myClass).selectAll('*').remove();

        // Compute brush-overview bar width from the smallest gap
        // between consecutive points in BRUSH coords (separate from
        // the main chart's bar width — the brush x-scale has its own
        // padded range).
        const brushPositions = allData
          .map((d: any) => xBrushAccessor(d) as number)
          .sort((a: number, b: number) => a - b);
        let minBrushStep = Infinity;
        for (let i = 1; i < brushPositions.length; i++) {
          const step = brushPositions[i] - brushPositions[i - 1];
          if (step > 0 && step < minBrushStep) minBrushStep = step;
        }
        if (!Number.isFinite(minBrushStep)) minBrushStep = chartWidth;
        const brushBarWidth = Math.max(1, Math.min(16, minBrushStep * 0.7));

        const buildBrushBarPath = (value: number): string => {
          let h = layout.brushRectH - yScaleBrush(value);
          if (h < 2 && h !== 0) h = 2;
          // Flat top with small fixed corners — matches the main bars /
          // small-bar-chart, instead of a brushBarWidth/2 dome.
          const r = Math.min(2, brushBarWidth / 2, h);
          return (
            'M0,' + h +
            'L0,' + r +
            ' Q0,0 ' + r + ',0' +
            ' L' + (brushBarWidth - r) + ',0' +
            ' Q' + brushBarWidth + ',0 ' + brushBarWidth + ',' + r +
            ' L' + brushBarWidth + ',' + h + ' Z'
          );
        };

        mySvg
          .select('#brushBarsGroup' + myClass)
          .attr('transform', brushTransform)
          .attr('clip-path', `url(#bmlBrushClip${myClass})`)
          .selectAll('.brushBar' + myClass)
          .data(allData, (_d: any, i: number) => i)
          .join(
            (enter: any) =>
              enter
                .append('path')
                .attr('class', 'brushBar' + myClass),
            (update: any) => update,
            (exit: any) => exit.remove(),
          )
          .attr('d', (d: any) => buildBrushBarPath(d.y))
          .attr('fill', props.color)
          .attr(
            'transform',
            (d: any) =>
              'translate(' +
              ((xBrushAccessor(d) as number) - brushBarWidth / 2) +
              ',' +
              yScaleBrush(d.y) +
              ')',
          );
      } else {
        // ── Brush overview as a line + dots (default). Strip any
        //    leftover bars so toggling chartType cleans up. ────────
        mySvg.select('#brushBarsGroup' + myClass).selectAll('*').remove();

        // mini line + dots (overview)
        mySvg
          .select('#brushLinesGroup' + myClass)
          .selectAll('#brushLinePath' + myClass)
          .data([allData])
          .join(
            (enter: any) => enter.append('path').attr('id', 'brushLinePath' + myClass),
            (update: any) => update,
          )
          .attr('d', lineBrush(allData))
          .attr('fill', 'transparent')
          .attr('stroke', props.color)
          .attr('stroke-width', 2)
          .attr('clip-path', `url(#bmlBrushClip${myClass})`)
          .attr('transform', brushTransform);

        mySvg
          .select('#brushDotsGroup' + myClass)
          .selectAll('.dotbrush' + myClass)
          .data(allData, (_d: any, i: number) => i)
          .join(
            (enter: any) =>
              enter
                .append('circle')
                .attr('class', 'dotbrush' + myClass)
                .attr('r', 2)
                .attr('stroke', props.color)
                .attr('stroke-width', 1.5)
                .attr('fill', '#fff')
                .attr('clip-path', `url(#bmlBrushClip${myClass})`),
            (update: any) => update,
            (exit: any) => exit.remove(),
          )
          .attr('cx', xBrushAccessor)
          .attr('cy', (d: any) => yScaleBrush(d.y))
          .attr('transform', brushTransform);
      }

      // Default the brush to a RECENT window so the chart opens with a
      // limited, readable set of bars (like small-bar-chart) instead of
      // hundreds of razor-thin bars spanning the full range. The window
      // size is derived from the chart width targeting the bar's max width
      // (~40px), so bars open at a comfortable size; dragging the brush
      // wider reveals the full history. Falls back to the full range when
      // there are few enough points to show readably at once.
      let defaultSelection: [number, number] = [0, chartWidth];
      if (isDateAxis && dateGroupDates.length > 1) {
        const targetSlotPx = 57; // ~40px bar (0.7 × 57) + breathing room
        const visibleCount = Math.max(6, Math.floor(chartWidth / targetSlotPx));
        if (dateGroupDates.length > visibleCount) {
          const startDate = dateGroupDates[dateGroupDates.length - visibleCount];
          const lastDate = dateGroupDates[dateGroupDates.length - 1];
          const xStartPx = xScaleAll(startDate) as number;
          const xLastPx = xScaleAll(lastDate) as number;
          // End the window just past the last bar instead of at chartWidth.
          // chartWidth maps to the full 2-period domain padding, which — once
          // zoomed into ~visibleCount periods — shows up as a big empty band
          // on the right. Use ~0.6 of a period of breathing room on each end
          // so the window hugs the bars.
          const margin =
            ((xLastPx - xStartPx) / Math.max(1, visibleCount - 1)) * 0.6;
          const x0 = Math.max(0, xStartPx - margin);
          const x1 = Math.min(chartWidth, xLastPx + margin);
          defaultSelection = [x0, x1];
        }
      }

      // brush itself (selection rect + handles)
      mySvg
        .select('#brushGroup' + myClass)
        .attr('transform', brushTransform)
        .call(brush)
        .call(brush.move, defaultSelection);

      mySvg
        .selectAll('.handleLines' + myClass)
        .attr('y1', brushY + (layout.brushRectH - 12) / 2)
        .attr('y2', brushY + (layout.brushRectH + 12) / 2)
        .style('stroke', '#8A98AB')
        .style('stroke-width', 1)
        .attr('transform', `translate(${layout.left},0)`);

      mySvg
        .select('#brushGroup' + myClass)
        .selectAll('.selection')
        .attr('fill', '#A0A0A0')
        .attr('fill-opacity', 0.2)
        .style('stroke', '#E8EAEE')
        .style('stroke-width', 1);

    } else {
      mySvg.select('#brushGroup' + myClass).selectAll('*').remove();
      mySvg.select('#brushLinesGroup' + myClass).selectAll('*').remove();
      mySvg.select('#brushDotsGroup' + myClass).selectAll('*').remove();
      mySvg.select('#brushBarsGroup' + myClass).selectAll('*').remove();
      mySvg.selectAll('.handleLines' + myClass).style('stroke-width', 0);
    }

    drawChart();

    // ─── inner: data-layer renderer (called on init + every brush change) ──
    function drawChart() {
      const xAxisRaw: any = d3.axisBottom(xScale).tickSizeOuter(0);
      if (xTickFormat) {
        if (tickValues) {
          // 1) Filter to the currently visible (brushed) domain.
          const [d0, d1] = xScale.domain() as [Date, Date];
          const visibleTicks = tickValues.filter((t) => t >= d0 && t <= d1);

          // 2) Subsample only when even slanted labels would overlap.
          //    Slanted labels need ~18px between anchors (label height /
          //    sin 45°) before they collide — set the budget right there
          //    so weekly / daily series show every period, not every other
          //    one. The rotation block below then slants them when they
          //    wouldn't fit upright. Keep first + last so the axis range
          //    is always bookended by a labelled tick.
          const MIN_PX_PER_TICK = 18;
          const maxTicks = Math.max(2, Math.floor(chartWidth / MIN_PX_PER_TICK));
          let displayTicks = visibleTicks;
          if (visibleTicks.length > maxTicks) {
            const step = Math.ceil(visibleTicks.length / maxTicks);
            displayTicks = visibleTicks.filter((_, i) => i % step === 0);
            const last = visibleTicks[visibleTicks.length - 1];
            if (displayTicks[displayTicks.length - 1] !== last) {
              // Pin the final tick so the right edge of the axis is always
              // labelled — but if the kept-tick before it would render too
              // close (e.g. "15 May 26" + "22 May 26"), swap it out instead
              // of appending so the labels don't overlap.
              const prevTick = displayTicks[displayTicks.length - 1];
              const prevX = xScale(prevTick) as number;
              const lastX = xScale(last) as number;
              if (lastX - prevX < MIN_PX_PER_TICK) {
                displayTicks[displayTicks.length - 1] = last;
              } else {
                displayTicks.push(last);
              }
            }
          }
          xAxisRaw.tickValues(displayTicks);
        }
        if (tickCount != null) xAxisRaw.ticks(tickCount);
        xAxisRaw.tickFormat(xTickFormat as any);
      }

      mySvg
        .select('#xAxis' + myClass)
        .call(xAxisRaw)
        .attr('transform', 'translate(' + layout.left + ',' + chartBottomY + ')')
        // NOTE: no clip-path here. `displayTicks` is already filtered to the
        // visible [d0, d1] domain, so out-of-range ticks never render — the
        // clip is redundant for that. Worse, with the 2-period domain padding
        // the first/last tick sits a few px from the chart edge, and a slanted
        // ("rotate(-45)", anchor end) label extends past x=0 / chartWidth — the
        // clip-rect (sized to the chart area) would chop those edge labels off.
        .attr('clip-path', null);

      mySvg.selectAll('#xAxis' + myClass + ' line').attr('display', 'none');
      mySvg.selectAll('.axis' + myClass + ' path').style('stroke', '#E8EAEE');
      mySvg
        .selectAll('.axis' + myClass + ' text')
        .style('font-family', 'Poppins')
        .style('font-weight', 500)
        .style('font-size', 10);
      // X-axis tick labels: push down ~12px below the axis line so the
      // text doesn't paint right on top of the axis stroke (default
      // d3 `y=9` + our font-size combo was visually overlapping). The
      // rotation block below overrides this for slanted labels.
      mySvg
        .selectAll('#xAxis' + myClass + ' text')
        .attr('y', 12)
        .attr('dy', '0.71em');

      // ── X-axis label rotation (mirrors combo-bar-chart). When the
      //    rendered labels would overflow the per-tick width budget, rotate
      //    them -45° with `text-anchor: end` so a dense weekly / daily axis
      //    stays readable instead of overlapping. Skipped when
      //    `d3AxisFormatting === true` because d3's auto-ticks are spaced
      //    out enough already. ─────────────────────────────────────────────
      if (!props.d3AxisFormatting) {
        const xTexts = mySvg.selectAll('#xAxis' + myClass + ' text');
        let maxTextWidth = 0;
        xTexts.each(function (this: any) {
          const bbox = (d3.select(this).node() as SVGGraphicsElement).getBBox();
          if (bbox.width > maxTextWidth) maxTextWidth = bbox.width;
        });

        const tickPositions: number[] = (xAxisRaw.tickValues() || []).map(
          (t: any) => xScale(t) as number,
        );
        let perTickBudget = chartWidth;
        if (tickPositions.length > 1) {
          let minGap = Infinity;
          for (let i = 1; i < tickPositions.length; i++) {
            const gap = Math.abs(tickPositions[i] - tickPositions[i - 1]);
            if (gap > 0 && gap < minGap) minGap = gap;
          }
          if (Number.isFinite(minGap)) perTickBudget = minGap;
        }

        if (maxTextWidth > perTickBudget - 5) {
          xTexts
            .style('text-anchor', 'end')
            .attr('y', 8)
            .attr('dx', '-0.4em')
            .attr('dy', '0.15em')
            .attr('transform', 'rotate(-45)');
        } else {
          xTexts
            .style('text-anchor', 'middle')
            .attr('dx', 0)
            .attr('dy', '0.71em')
            .attr('y', 12)
            .attr('transform', null);
        }
      }

      // ── Reposition the "Period" caption AND the brush row right below
      //    the actual rendered tick text. The layout reserves space for
      //    the worst case (slant predicted), but when labels render
      //    upright they only occupy ~14px — leaving a stranded empty
      //    band between caption and brush. Measuring the rendered xAxis
      //    bbox lets us slide both elements up to close that gap, no
      //    matter whether slanting actually triggered. ────────────────
      const xAxisNode = (mySvg.select('#xAxis' + myClass).node() as SVGGraphicsElement | null);
      if (xAxisNode) {
        const xAxisBBox = xAxisNode.getBBox();
        // xAxis group is translated to chartBottomY, so bbox.y is
        // relative to that translation; absolute bottom = chartBottomY
        // + bbox.y + bbox.height.
        const tickBottomAbs = chartBottomY + xAxisBBox.y + xAxisBBox.height;

        // 1) Caption — sits ~10px below the deepest tick label. Capped
        //    above the (eventual) brush position so it can never cross
        //    into the brush row.
        const captionGap = 10;
        const labelTextHeight = 12;
        let dynamicLabelY = tickBottomAbs + captionGap;
        if (props.xAxisLabel) {
          mySvg.select('#xAxisLabel' + myClass).attr('y', dynamicLabelY);
        }

        // 2) Brush — start `gap` pixels below the caption baseline (or
        //    below the tick text if there's no caption). Capped at the
        //    pre-reserved `brushY` so we only ever shift it UPWARD; the
        //    layout already guaranteed enough room for the worst case,
        //    so moving up just reclaims surplus.
        if (showBrush) {
          const brushGap = props.xAxisLabel ? 12 : 14;
          const captionBottom = props.xAxisLabel
            ? dynamicLabelY + (labelTextHeight - 8)
            : tickBottomAbs;
          const dynamicBrushY = Math.min(brushY, captionBottom + brushGap);

          if (Math.abs(dynamicBrushY - brushY) > 1) {
            const newBrushTransform = `translate(${layout.left},${dynamicBrushY})`;
            mySvg.selectAll('#brushLinePath' + myClass).attr('transform', newBrushTransform);
            mySvg.selectAll('.dotbrush' + myClass).attr('transform', newBrushTransform);
            mySvg.select('#brushBarsGroup' + myClass).attr('transform', newBrushTransform);
            mySvg.select('#brushGroup' + myClass).attr('transform', newBrushTransform);
            mySvg
              .selectAll('.handleLines' + myClass)
              .attr('y1', dynamicBrushY + (layout.brushRectH - 12) / 2)
              .attr('y2', dynamicBrushY + (layout.brushRectH + 12) / 2);
          }
        }
      }

      // ── Hover helpers (used by BOTH the line path and the dots, matching
      //    the source `basic-line-chart-grid` pattern). The vertical guide
      //    snaps to the nearest data point's x; the tooltip shows that
      //    point's period + value. ──────────────────────────────────────
      const bisectX = d3.bisector((d: any) =>
        isDateAxis ? (d.x as Date).getTime() : 0,
      ).center;

      const showHoverFor = (event: any, d: any) => {
        const svgX = layout.left + (xAccessor(d) as number);
        mySvg
          .select('#hoverLine' + myClass)
          .attr('visibility', 'visible')
          .attr('x1', svgX)
          .attr('x2', svgX)
          .attr('y1', chartTopY)
          .attr('y2', chartBottomY)
          .raise();
        myChart.showTooltip(event, d);
      };

      const hideHover = () => {
        mySvg.select('#hoverLine' + myClass).attr('visibility', 'hidden');
        myChart.hideTooltip();
      };

      const barsGroup = mySvg
        .select('#barsGroup' + myClass)
        .attr('transform', 'translate(' + layout.left + ',' + chartTopY + ')')
        // Apply the chart-area clip-path at the group level. The per-bar
        // `<g>` transforms shift the clip rect's local space if it's set
        // on each bar — clipping at the stable parent keeps the clip
        // aligned with the chart's x extent, so bars that scale outside
        // the visible (brushed) domain are properly hidden.
        .attr('clip-path', 'url(#bmlBrushClip' + myClass + ')');

      if (props.chartType === 'bar') {
        // ── Bars (combo-bar-chart styling) ─────────────────────────────
        // Compute a uniform bar width from the smallest gap between
        // consecutive points (so weekly/monthly/quarterly series all get
        // sensibly-sized bars). Cap at 40px so a sparse series doesn't
        // produce one giant block.
        const positions = allData
          .map((d: any) => xAccessor(d) as number)
          .sort((a: number, b: number) => a - b);
        let minStep = Infinity;
        for (let i = 1; i < positions.length; i++) {
          const step = positions[i] - positions[i - 1];
          if (step > 0 && step < minStep) minStep = step;
        }
        if (!Number.isFinite(minStep)) minStep = chartWidth;
        const barWidth = Math.max(2, Math.min(40, minStep * 0.7));

        // Strip the line + dots so they don't paint underneath the bars.
        mySvg
          .select('#linePath' + myClass)
          .attr('d', null)
          .style('pointer-events', 'none')
          .on('mousemove', null)
          .on('mouseout', null);
        mySvg.select('#dotsGroup' + myClass).selectAll('.dot' + myClass).remove();

        // Bar shape mirrors `small-bar-chart.component`: a flat top with a
        // small fixed corner radius (2px) — NOT a `barWidth/2` dome. The
        // radius is clamped so very thin or very short bars don't produce a
        // degenerate path.
        const buildBarPath = (value: number): string => {
          let barHeight = chartHeight - yScale(value);
          if (barHeight < 5 && barHeight !== 0) barHeight = 5;
          const r = Math.min(2, barWidth / 2, barHeight);
          return (
            'M0,' + barHeight +
            'L0,' + r +
            ' Q0,0 ' + r + ',0' +
            ' L' + (barWidth - r) + ',0' +
            ' Q' + barWidth + ',0 ' + barWidth + ',' + r +
            ' L' + barWidth + ',' + barHeight + ' Z'
          );
        };

        const barGroupSel = barsGroup
          .selectAll('.barRectGroup' + myClass)
          .data(allData, (_d: any, i: number) => i)
          .join(
            (enter: any) => {
              const g = enter
                .append('g')
                .attr('class', 'barRectGroup' + myClass);
              // Clip-path is applied on the parent `barsGroup` so the
              // bar shapes are clipped against the chart's x extent
              // (per-bar clip-path would shift with each bar's
              // transform and never align).
              g.append('path').attr('class', 'barRect' + myClass);
              return g;
            },
            (update: any) => update,
            (exit: any) => exit.remove(),
          )
          .attr(
            'transform',
            (d: any) =>
              'translate(' +
              ((xAccessor(d) as number) - barWidth / 2) +
              ',' +
              yScale(d.y) +
              ')',
          );

        barGroupSel
          .select('.barRect' + myClass)
          .attr('d', (d: any) => buildBarPath(d.y))
          .attr('fill', props.color);

        // Hover: dim sibling bars + show horizontal value guide line that
        // runs from the bar to the right edge (combo-bar's `barMouseoverLine`).
        barGroupSel
          .on('mousemove', function (this: any, event: any, d: any) {
            barsGroup
              .selectAll('.barRectGroup' + myClass)
              .attr('opacity', 0.4);
            d3.select(this).attr('opacity', 1);
            // Horizontal guide line at the bar's top, full chart width.
            mySvg
              .select('#hoverLine' + myClass)
              .attr('visibility', 'visible')
              .attr('x1', layout.left)
              .attr('x2', layout.left + chartWidth)
              .attr('y1', chartTopY + yScale(d.y))
              .attr('y2', chartTopY + yScale(d.y))
              .attr('stroke-dasharray', '2,2')
              .raise();
            myChart.showTooltip(event, d);
          })
          .on('mouseout', () => {
            barsGroup
              .selectAll('.barRectGroup' + myClass)
              .attr('opacity', 1);
            mySvg
              .select('#hoverLine' + myClass)
              .attr('visibility', 'hidden')
              .attr('stroke-dasharray', '4,4'); // restore line-mode dash
            myChart.hideTooltip();
          });
      } else {
        // ── Line + dots (default) ──────────────────────────────────────
        // Strip any bars from a previous render so toggling chartType at
        // runtime doesn't leave stale shapes behind.
        barsGroup.selectAll('.barRectGroup' + myClass).remove();

        // ── Main line — pointer-events: stroke means only the actual stroke
        //    pixels catch the mousemove (same as source). On hover, bisect
        //    the data array to find the nearest point and snap the guide. ──
        mySvg
          .select('#linePath' + myClass)
          .attr('clip-path', 'url(#bmlBrushClip' + myClass + ')')
          .attr('d', line(allData))
          .attr('fill', 'transparent')
          .style('pointer-events', 'stroke')
          .attr('stroke', props.color)
          .attr('stroke-width', props.lineWidth)
          .attr('transform', 'translate(' + layout.left + ',' + chartTopY + ')')
          .on('mousemove', function (this: any, event: any) {
            if (!allData.length) return;
            const [mouseX] = d3.pointer(event, mySvg.node());
            const cx = mouseX - layout.left; // chart-local x
            let nearest: any;
            if (isDateAxis) {
              const tDate = xScale.invert(cx);
              const i = bisectX(allData, tDate.getTime());
              nearest = allData[Math.max(0, Math.min(allData.length - 1, i))];
            } else {
              let bestIx = 0;
              let bestDist = Infinity;
              allData.forEach((d: any, idx: number) => {
                const dist = Math.abs((xAccessor(d) as number) - cx);
                if (dist < bestDist) {
                  bestDist = dist;
                  bestIx = idx;
                }
              });
              nearest = allData[bestIx];
            }
            if (nearest) showHoverFor(event, nearest);
          })
          .on('mouseout', hideHover);

        // ── Dots ────────────────────────────────────────────────────────
        const dotsGroup = mySvg
          .select('#dotsGroup' + myClass)
          .attr('transform', 'translate(' + layout.left + ',' + chartTopY + ')');

        if (props.showDots) {
          dotsGroup
            .selectAll('.dot' + myClass)
            .data(allData, (_d: any, i: number) => i)
            .join(
              (enter: any) =>
                enter
                  .append('circle')
                  .attr('class', 'dot' + myClass)
                  .attr('r', props.dotRadius)
                  .attr('stroke', props.color)
                  .attr('stroke-width', 3)
                  .attr('fill', '#fff')
                  .attr('clip-path', 'url(#bmlBrushClip' + myClass + ')'),
              (update: any) => update,
              (exit: any) => exit.remove(),
            )
            .attr('cx', xAccessor)
            .attr('cy', (d: any) => yScale(d.y))
            .on('mousemove', (event: any, d: any) => showHoverFor(event, d))
            .on('mouseout', hideHover);
        } else {
          dotsGroup.selectAll('.dot' + myClass).remove();
        }
      }

      // The capture rect was used by a previous iteration to catch
      // hover-anywhere mousemoves. The source chart doesn't have one;
      // strip its handlers so it never swallows events from the line/dots.
      mySvg
        .select('#hoverCapture' + myClass)
        .attr('width', 0)
        .attr('height', 0)
        .on('mousemove', null)
        .on('mouseleave', null);
    }

    // ─── inner: brush callback ──────────────────────────────────────────────
    function brushed(event: any) {
      const selection = event.selection;
      if (!selection) return;

      let [x0, x1] = selection;
      x0 = Math.max(0, x0);
      x1 = Math.min(chartWidth, x1);

      const MIN_WIDTH_PX = 40;
      if (x1 - x0 < MIN_WIDTH_PX) {
        x1 = x0 + MIN_WIDTH_PX;
        if (x1 > chartWidth) {
          x1 = chartWidth;
          x0 = x1 - MIN_WIDTH_PX;
        }
        if (brush) {
          d3.select('#brushGroup' + myClass).call(brush.move as any, [x0, x1]);
        }
        return;
      }

      myChart.hideTooltip();
      mySvg.select('#hoverLine' + myClass).attr('visibility', 'hidden');

      const xDomain = [xScaleAll.invert(x0), xScaleAll.invert(x1)];
      xScale.domain(xDomain);
      drawChart();

      const brushGroup = mySvg.select('#brushGroup' + myClass);
      const handleEastX = +brushGroup.select('.handle--e').attr('x') || 0;
      const handleWestX = +brushGroup.select('.handle--w').attr('x') || 0;

      mySvg.select('.handleLeftLine1' + myClass).attr('x1', handleEastX + 3).attr('x2', handleEastX + 3);
      mySvg.select('.handleLeftLine2' + myClass).attr('x1', handleEastX + 7).attr('x2', handleEastX + 7);
      mySvg.select('.handleRightLine1' + myClass).attr('x1', handleWestX + 3).attr('x2', handleWestX + 3);
      mySvg.select('.handleRightLine2' + myClass).attr('x1', handleWestX + 7).attr('x2', handleWestX + 7);

      brushGroup
        .selectAll('.handle')
        .attr('fill', 'white')
        .attr('rx', 4)
        .attr('ry', 4)
        .attr('y', (layout.brushRectH - 24) / 2)
        .attr('height', 24)
        .style('filter', 'url(#d3blp-drop-shadow)');
    }
  }

  // ─── helpers ──────────────────────────────────────────────────────────────

  private makeYTickFormatter(): (n: number) => string {
    if (this.props.valueFormat === 'percentage') {
      return (n: number) => `${this.trimNumber(n)}%`;
    }
    try {
      const fmt = d3.format(this.props.yAxisFormat || '.2~s');
      return (n: number) => fmt(n);
    } catch {
      return (n: number) => String(n);
    }
  }

  private formatYValueForTooltip(n: number): string {
    if (this.props.valueFormat === 'percentage') {
      return `${this.trimNumber(n)}%`;
    }
    return String(n);
  }

  private trimNumber(n: number): string {
    if (!Number.isFinite(n)) return '';
    if (Number.isInteger(n)) return String(n);
    return String(Math.round(n * 100) / 100);
  }

  private showTooltip(event: any, d: any): void {
    const isDate = d.x instanceof Date && !isNaN(d.x.getTime());
    // Format the tooltip date with the SAME d3.timeFormat string used
    // for the x-axis ticks, so the period label in the tooltip matches
    // what the user sees on the axis (e.g. '06 Jan 25' for W, 'Jan 2025'
    // for M / Q, '2025' for Y, 'Q1 2025' for PCQ).
    let dateLabel: string | null = null;
    if (isDate) {
      const period = (this.props.period || 'M') as D3BasicLinePeriod;
      const fmtStr = this.tickFormatMap[period] || '%b %Y';
      dateLabel = d3.timeFormat(fmtStr)(d.x as Date);
    }
    this.mytooltipData = [
      {
        colour: this.props.color,
        label: this.props.yAxisLabel || 'Value',
        value: this.formatYValueForTooltip(d.y),
        dateLabel,
        periodLabel: isDate ? null : String(d.x),
      },
    ];
    this.tooltipVisible = true;
    // Render the new content (OnPush) so the element has its final size before
    // we measure + position it.
    this.cdr.detectChanges();
    this.positionTooltip(event);
  }

  /** Position the in-component tooltip to follow the cursor. The tooltip is a
   *  plain absolutely-positioned div living inside `.dialog-body` (its
   *  positioned ancestor) — NOT a CDK overlay — so it can never stack behind
   *  the Material dialog the way the overlay-service tooltip did (mirrors
   *  small-bar-chart's `#d3SBarTooltip`). It flips to the other side of the
   *  cursor near the right / bottom edges so it stays inside the dialog body. */
  private positionTooltip(event: any): void {
    const tipEl: HTMLElement | undefined = this.linesChartTooltip?.nativeElement;
    const parent = tipEl?.offsetParent as HTMLElement | null; // .dialog-body
    if (!tipEl || !parent) return;

    const pRect = parent.getBoundingClientRect();
    const tipW = tipEl.offsetWidth;
    const tipH = tipEl.offsetHeight;
    const offset = 14;

    const cursorX = event.clientX - pRect.left;
    const cursorY = event.clientY - pRect.top;

    // Default below-right of the cursor; flip if it would overflow the body.
    let left = cursorX + offset;
    if (left + tipW > pRect.width) left = cursorX - offset - tipW;
    if (left < 0) left = 4;

    let top = cursorY + offset;
    if (top + tipH > pRect.height) top = cursorY - offset - tipH;
    if (top < 0) top = 4;

    tipEl.style.left = `${left}px`;
    tipEl.style.top = `${top}px`;
  }

  private hideTooltip(): void {
    this.tooltipVisible = false;
    this.mytooltipData = null;
    this.cdr.markForCheck();
  }

  private toDate(v: any): Date | string {
    if (v instanceof Date) return v;
    if (v === null || v === undefined) return '';
    if (typeof v === 'number') return v as any;
    const d = new Date(v);
    return isNaN(d.getTime()) ? String(v) : d;
  }
}
